import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { AssignableUser, DiagramVersionDetail, DiagramVersionSummary, PdfExportSettings, PermissionLevel, UserRef } from '../api/types';
import { TaskTable } from '../components/TaskTable';
import { CalendarSettingsModal } from '../components/CalendarSettingsModal';
import { PdfExportSettingsModal, DEFAULT_SETTINGS as DEFAULT_PDF_SETTINGS } from '../components/PdfExportSettingsModal';
import { GanttChart } from '../components/GanttChart';
import { beginSave, endSave, confirmLeaveIfPending, markSaved, SAVE_CONFIRMED_EVENT } from '../utils/pendingSave';
import { nextVersionLabel } from '../utils/versionLabel';
import { generateDiagramPdf } from '../utils/pdfExport';
import { useAuth } from '../contexts/AuthContext';

type AssigneeField = 'projectManagerId' | 'drafterId' | 'siteManagerId' | 'techComponentsId';

/** Un sélecteur par rôle assignable, dans l'ordre d'affichage de la barre du diagramme. */
const ASSIGNEE_ROLES: { field: AssigneeField; icon: string; labelKey: string }[] = [
  { field: 'projectManagerId', icon: '🧑‍💼', labelKey: 'diagram.projectManager' },
  { field: 'drafterId', icon: '✏️', labelKey: 'diagram.drafter' },
  { field: 'siteManagerId', icon: '🏗️', labelKey: 'diagram.siteManager' },
  { field: 'techComponentsId', icon: '🔧', labelKey: 'diagram.techComponents' },
];

interface DiagramDetail {
  id: string;
  name: string;
  folderId: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  version: number;
  content: Record<string, unknown>;
  permissionLevel: PermissionLevel;
  projectManagerId: string | null;
  drafterId: string | null;
  siteManagerId: string | null;
  techComponentsId: string | null;
  projectManager: UserRef | null;
  drafter: UserRef | null;
  siteManager: UserRef | null;
  techComponents: UserRef | null;
  updatedAt: string;
}

export function DiagramPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { diagramId } = useParams();
  const navigate = useNavigate();
  const [diagram, setDiagram] = useState<DiagramDetail | null>(null);
  const [assignableUsers, setAssignableUsers] = useState<AssignableUser[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [versions, setVersions] = useState<DiagramVersionSummary[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [versionDetail, setVersionDetail] = useState<DiagramVersionDetail | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showPdfExportModal, setShowPdfExportModal] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');

  const loadDiagram = () => {
    if (!diagramId) return;
    api
      .get<DiagramDetail>(`/diagrams/${diagramId}`)
      .then((d) => {
        setDiagram(d);
        setLastSavedAt(new Date(d.updatedAt));
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)));
  };

  const loadVersions = () => {
    if (!diagramId) return;
    api.get<DiagramVersionSummary[]>(`/diagrams/${diagramId}/versions`).then(setVersions);
  };

  const loadAssignableUsers = () => {
    if (!diagramId) return;
    api.get<AssignableUser[]>(`/diagrams/${diagramId}/assignable-users`).then(setAssignableUsers);
  };

  useEffect(() => {
    loadDiagram();
    loadVersions();
    loadAssignableUsers();
    setSelectedVersionId(null);
    setVersionDetail(null);
  }, [diagramId]);

  // Reflète l'heure de sauvegarde confirmée par le serveur pour CETTE page,
  // qu'elle vienne d'ici (assignation) ou du tableau de tâches (TaskTable).
  useEffect(() => {
    const handler = (e: Event) => setLastSavedAt(new Date((e as CustomEvent<{ at: string }>).detail.at));
    window.addEventListener(SAVE_CONFIRMED_EVENT, handler);
    return () => window.removeEventListener(SAVE_CONFIRMED_EVENT, handler);
  }, []);

  const canWrite = diagram?.permissionLevel === 'WRITE' || diagram?.permissionLevel === 'ADMIN';
  const viewingVersion = versions.find((v) => v.id === selectedVersionId) ?? null;
  // La révision la plus récente (versions triées par date de création
  // croissante côté serveur) n'apparaît jamais comme un instantané figé
  // séparé dans le menu déroulant : elle EST l'état courant, éditable — son
  // étiquette ("A - en cours", puis "B - en cours" après la révision
  // suivante...) sert seulement à nommer l'entrée "en direct" existante,
  // pas à en ajouter une deuxième.
  const latestVersion = versions.length ? versions[versions.length - 1] : null;
  const pastVersions = versions.length ? versions.slice(0, -1) : [];

  const handleAssigneeChange = async (field: AssigneeField, value: string) => {
    if (!diagramId || !diagram) return;
    setError(null);
    beginSave();
    try {
      const updated = await api.patch<DiagramDetail>(`/diagrams/${diagramId}`, { [field]: value || null });
      setDiagram(updated);
      markSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      endSave();
    }
  };

  const handleDuplicate = async () => {
    if (!diagramId) return;
    const copy = await api.post<DiagramDetail>(`/diagrams/${diagramId}/duplicate`);
    navigate(`/diagrams/${copy.id}`);
  };

  const handleExport = async () => {
    if (!diagramId || !diagram || exporting) return;
    setError(null);
    setExporting(true);
    try {
      const saved = await api.get<PdfExportSettings | null>(`/diagrams/${diagramId}/pdf-export-settings`);
      await generateDiagramPdf({
        diagramId,
        diagramName: diagram.name,
        settings: { ...DEFAULT_PDF_SETTINGS, ...saved },
        dateFormat: user?.dateFormat ?? 'DD-MM-YYYY',
        weekNumbering: user?.weekNumbering ?? 'ISO',
        locale: i18n.language,
        weekAbbrev: (n: number) => t('chart.weekAbbrev', { n }),
        columnLabel: (col: string) => t(`tasks.colShort.${col}`),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  };

  const handleCreateVersion = async () => {
    if (!diagramId) return;
    // Le tableau de tâches s'enregistre déjà automatiquement à chaque
    // modification (voir TaskTable) — cette icône, malgré son allure de
    // "sauvegarder" générique, fait tout autre chose : elle crée un
    // instantané nommé supplémentaire. Une confirmation explicite évite de
    // multiplier les révisions par un simple clic malentendu (voir demande
    // utilisateur : le changement de révision doit être à la demande de
    // l'utilisateur, jamais un effet de bord d'une sauvegarde ordinaire).
    const label = nextVersionLabel(versions.length);
    if (!confirm(t('diagram.saveVersionConfirm', { label }) as string)) return;
    setError(null);
    try {
      const created = await api.post<DiagramVersionSummary>(`/diagrams/${diagramId}/versions`);
      loadVersions();
      setMessage(t('diagram.versionSaved', { label: created.label }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  const handleSelectVersion = async (versionId: string) => {
    if (!diagramId || !versionId) {
      setSelectedVersionId(null);
      setVersionDetail(null);
      return;
    }
    // On charge l'instantané AVANT de faire pivoter le TaskTable en mode
    // lecture seule (son `key` change avec selectedVersionId) : sinon il se
    // remonterait avec overrideTasks encore undefined et irait chercher le
    // tableau EN DIRECT au lieu d'attendre cet instantané.
    const detail = await api.get<DiagramVersionDetail>(`/diagrams/${diagramId}/versions/${versionId}`);
    setVersionDetail(detail);
    setSelectedVersionId(versionId);
  };

  const handleRestoreVersion = async () => {
    if (!diagramId || !viewingVersion) return;
    if (!confirm(t('diagram.restoreVersionConfirm', { label: viewingVersion.label }) as string)) return;
    await api.post(`/diagrams/${diagramId}/versions/${viewingVersion.id}/restore`);
    setSelectedVersionId(null);
    setVersionDetail(null);
    setMessage(t('diagram.versionRestored', { label: viewingVersion.label }));
  };

  if (error) return <p className="error-text">{error}</p>;
  if (!diagram) return <p>{t('common.loading')}</p>;

  return (
    <div>
      <Link
        to={`/folders/${diagram.folderId}`}
        onClick={(e) => {
          if (!confirmLeaveIfPending(t('common.leaveWithPendingSave'))) e.preventDefault();
        }}
      >
        &larr; {t('diagram.backToFolder')}
      </Link>

      <div className="card diagram-toolbar" style={{ marginTop: 16, marginBottom: 4 }}>
        <strong className="diagram-name-display" title={t('diagram.renameHint') as string}>
          {diagram.name}
        </strong>
        <span className="badge">{diagram.status}</span>

        {ASSIGNEE_ROLES.map(({ field, icon, labelKey }) => (
          <Fragment key={field}>
            <span title={t(labelKey) as string}>{icon}</span>
            <select
              className="diagram-assignee-select"
              value={diagram[field] ?? ''}
              disabled={!canWrite}
              onChange={(e) => handleAssigneeChange(field, e.target.value)}
              title={t(labelKey) as string}
            >
              <option value="">{t('diagram.unassigned')}</option>
              {assignableUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName}
                </option>
              ))}
            </select>
          </Fragment>
        ))}

        <select
          className="diagram-version-select"
          value={selectedVersionId ?? ''}
          onChange={(e) => handleSelectVersion(e.target.value)}
          title={t('diagram.versionSelect') as string}
        >
          <option value="">
            {latestVersion ? t('diagram.currentVersionLabeled', { label: latestVersion.label }) : t('diagram.currentVersion')}
          </option>
          {[...pastVersions].reverse().map((v) => (
            <option key={v.id} value={v.id}>
              {v.label} — {new Date(v.createdAt).toLocaleString()}
            </option>
          ))}
        </select>
        {canWrite && (
          <button className="btn icon-btn" onClick={handleCreateVersion} title={t('diagram.saveVersion') as string}>
            💾
          </button>
        )}
        <button className="btn icon-btn" onClick={handleDuplicate} title={t('diagram.duplicate') as string}>
          📋
        </button>
        <button className="btn icon-btn" onClick={handleExport} disabled={exporting} title={t(exporting ? 'diagram.exporting' : 'diagram.exportPdf') as string}>
          📄
        </button>
        <button className="btn icon-btn" onClick={() => setShowCalendarModal(true)} title={t('calendar.title') as string}>
          📅
        </button>
        <button className="btn icon-btn" onClick={() => setShowPdfExportModal(true)} title={t('pdfExport.title') as string}>
          🖨️
        </button>
      </div>

      {lastSavedAt && (
        <p className="diagram-last-saved">{t('diagram.lastSaved', { time: lastSavedAt.toLocaleTimeString() })}</p>
      )}
      {message && <p className="success-text">{message}</p>}

      {viewingVersion && (
        <div className="card version-banner" style={{ marginBottom: 16 }}>
          <span>{t('diagram.viewingVersion', { label: viewingVersion.label, date: new Date(viewingVersion.createdAt).toLocaleString() })}</span>
          <div className="toolbar" style={{ margin: 0 }}>
            <button className="btn" onClick={() => handleSelectVersion('')}>
              {t('diagram.backToCurrent')}
            </button>
            {canWrite && (
              <button className="btn btn-danger" onClick={handleRestoreVersion}>
                {t('diagram.restoreVersion')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="toolbar" style={{ marginBottom: 8 }}>
        <button className={`btn ${view === 'table' ? 'btn-primary' : ''}`} onClick={() => setView('table')}>
          {t('diagram.viewTable')}
        </button>
        <button className={`btn ${view === 'chart' ? 'btn-primary' : ''}`} onClick={() => setView('chart')}>
          {t('diagram.viewChart')}
        </button>
      </div>

      {view === 'table' ? (
        <>
          <h3>{t('tasks.title')}</h3>
          <TaskTable
            key={selectedVersionId ?? 'live'}
            diagramId={diagram.id}
            canWrite={canWrite && !viewingVersion}
            overrideTasks={versionDetail?.tasksSnapshot}
          />
        </>
      ) : (
        <GanttChart
          key={`${selectedVersionId ?? 'live'}-chart`}
          diagramId={diagram.id}
          canWrite={canWrite && !viewingVersion}
          overrideTasks={versionDetail?.tasksSnapshot}
        />
      )}

      {showCalendarModal && (
        <CalendarSettingsModal diagramId={diagram.id} canWrite={canWrite} onClose={() => setShowCalendarModal(false)} />
      )}
      {showPdfExportModal && (
        <PdfExportSettingsModal diagramId={diagram.id} canWrite={canWrite} onClose={() => setShowPdfExportModal(false)} />
      )}
    </div>
  );
}
